import { faCalendarWeek, faCheckCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getTableDate } from "../../services/utils";
import { faCircle } from "@fortawesome/pro-regular-svg-icons";
import cx from "classnames";
import Loading from "../Loading/Loading";
import React from "react";
import Link from "../Link/Link";

interface IProps {
    konnunHafin: string | undefined;
    tengilidurSkradur?: string;
    konnunSend: string | undefined;
    konnunSvarad: string | undefined;
    ahaettumatSkrad: string | undefined;
    loading: boolean;
    type: 'individual' | 'company';
    link: string | undefined;
}

/**
 * Represents a single date field within a stage
 * Used for stages that can display multiple date values
 */
interface DateField {
    key: keyof Omit<IProps, 'link' | 'loading' | 'type'>; // The property name from props
    label: string; // The display label for this date field
    className?: string; // Optional additional CSS class for the label
    type?: 'string' | 'date'; // Type of the date field (default is 'date')
}

/**
 * Configuration for each stage in the KYC survey progress
 * Supports both single and multiple date fields per stage
 */
interface Stage {
    stageNumber: number; // The numeric identifier for this stage (used in the icon)
    dateFields: DateField[]; // Array of date fields - can be one or more
    finishedKey: keyof Omit<IProps, 'link' | 'loading' | 'type'>; // Which property determines if stage is complete
}

/**
 * KYCSurveyProgressBar Component
 * 
 * Displays a progress bar showing the current status of a KYC survey with multiple stages.
 * Each stage can have one or more date fields and shows:
 * - A completion icon (check or numbered circle)
 * - One or more stage labels with corresponding dates
 * - Calendar icons for each date field
 * 
 * The component is flexible to handle:
 * - Simple stages with one label and date
 * - Complex stages with multiple labels and dates
 * - Custom completion criteria for each stage
 * - Sequential completion (a stage can only be complete if all previous stages are complete)
 * - Won't show stages as complete while data is still loading
 * - Different stage configurations for individuals vs companies
 */
const KYCSurveyProgressBar: React.FC<IProps> = ({
    konnunHafin,
    tengilidurSkradur,
    konnunSend,
    ahaettumatSkrad,
    konnunSvarad,
    loading,
    type,
    link
}) => {
    // Define all stages in order with their configuration based on type
    // Individuals skip the "Tengiliður skráður" stage (stage 2 for companies)
    // Each stage can have multiple date fields, and we specify which field determines completion
    const stages: Stage[] = type === 'individual' ? [
        {
            stageNumber: 1,
            dateFields: [
                { key: 'konnunHafin', label: 'Könnun hafin' }
            ],
            finishedKey: 'konnunHafin' // Stage 1 is complete when konnunHafin has a value
        },
        {
            stageNumber: 2,
            // This stage has TWO date fields displayed side by side
            dateFields: [
                { key: 'konnunSend', label: 'Könnun send' },
                { key: 'konnunSvarad', label: 'Könnun svarað', className: 'konnun_svarad' }
            ],
            finishedKey: 'konnunSvarad' // Stage 2 is complete when konnunSvarad has a value
        },
        {
            stageNumber: 3,
            dateFields: [
                { key: 'ahaettumatSkrad', label: 'Áhættumat skráð' }
            ],
            finishedKey: 'ahaettumatSkrad' // Stage 3 is complete when ahaettumatSkrad has a value
        }
    ] : [
        // Company stages include the "Tengiliður skráður" stage
        {
            stageNumber: 1,
            dateFields: [
                { key: 'konnunHafin', label: 'Könnun hafin' }
            ],
            finishedKey: 'konnunHafin' // Stage 1 is complete when konnunHafin has a value
        },
        {
            stageNumber: 2,
            dateFields: [
                { key: 'tengilidurSkradur', label: 'Tengiliður skráður', type: 'string' }
            ],
            finishedKey: 'tengilidurSkradur' // Stage 2 is complete when tengilidurSkradur has a value
        },
        {
            stageNumber: 3,
            // This stage has TWO date fields displayed side by side
            dateFields: [
                { key: 'konnunSend', label: 'Könnun send' },
                { key: 'konnunSvarad', label: 'Könnun svarað', className: 'konnun_svarad' }
            ],
            finishedKey: 'konnunSvarad' // Stage 3 is complete when konnunSvarad has a value
        },
        {
            stageNumber: 4,
            dateFields: [
                { key: 'ahaettumatSkrad', label: 'Áhættumat skráð' }
            ],
            finishedKey: 'ahaettumatSkrad' // Stage 4 is complete when ahaettumatSkrad has a value
        }
    ];

    // Map props to an object for easier access by key
    // This allows us to dynamically look up date values using the keys defined in our stage configuration
    const stageData = {
        konnunHafin,
        tengilidurSkradur,
        konnunSend,
        ahaettumatSkrad,
        konnunSvarad
    };

    /**
     * Determines if a stage should be marked as finished
     * A stage is only finished if:
     * 1. We're not in a loading state (data has been fully fetched), AND
     * 2. Its designated finishedKey has a value, AND
     * 3. All previous stages are also finished (sequential completion)
     * 
     * @param stageIndex - The index of the stage in the stages array
     * @returns true if the stage should show as completed, false otherwise
     */
    const isStageFinished = (stageIndex: number): boolean => {
        // If we're still loading data, no stages should show as finished
        // This prevents the "completed" checkmark from appearing before we have the actual data
        if (loading) {
            return false;
        }

        // Check if the current stage's finishedKey has a value
        const currentStageFinished = !!stageData[stages[stageIndex].finishedKey];

        // If this is the first stage, we only need to check if it has a value
        if (stageIndex === 0) {
            return currentStageFinished;
        }

        // For subsequent stages, check if all previous stages are finished
        // We do this recursively to ensure the entire chain is complete
        const previousStageFinished = isStageFinished(stageIndex - 1);

        // This stage is only finished if both conditions are met:
        // 1. The current stage has its required data
        // 2. All previous stages are complete
        return currentStageFinished && previousStageFinished;
    };

    return (
        <div className={cx("KCL_KYCSurveyProgressBar", type)}>
            <div className="surveyStage_wrapper-test" >
                <div className="surveyStage_wrapper" >
                    <p className="paragraph--bold title">Staða könnunar:</p>

                    {/* Render each stage dynamically from the configuration */}
                    {stages.map((stage, stageIndex) => {
                        // Determine if this stage is finished using sequential logic
                        // This ensures stages can't show as complete if earlier stages aren't done
                        // and won't show as complete while we're still loading data
                        const isFinished = isStageFinished(stageIndex);

                        return (
                            <div key={`stage-${stage.stageNumber}`} className={cx("surveyStage_wrapper__stage", stage.finishedKey)}>
                                {/* Stage completion icon - shows check if finished, numbered circle if not */}
                                <StageIcon finished={isFinished} stageNumber={stage.stageNumber} />
                                <div className="value-and-title-wrapper">

                                    {/* Render all date fields for this stage */}
                                    {stage.dateFields.map((field) => {
                                        // Get the actual date value for this field
                                        const dateValue = stageData[field.key];

                                        return (
                                            <div className="value-and-title" key={field.key}>
                                                {/* Stage label */}
                                                <p className={cx("stage_title", field.className)}>
                                                    {field.label}
                                                </p>

                                                <div className="stage_value">
                                                    {/* Calendar icon - only show for date type fields */}
                                                    {field.type !== "string" &&
                                                        <FontAwesomeIcon className="fa-icon calendar" icon={faCalendarWeek} />
                                                    }

                                                    {/* Formatted date or string value */}
                                                    {/* Shows loading skeleton when parent component is loading */}
                                                    {/* Adds 'has-no-value' class when date is empty for styling */}
                                                    <p className={cx("stage_date", field.key, {
                                                        'has-no-value': !dateValue,
                                                    })}>
                                                        {loading ? (
                                                            // Show loading skeleton while data is being fetched
                                                            <Loading count={1} width={80} />
                                                        ) : (
                                                            // Show the actual value once loaded
                                                            field.type === 'string'
                                                                ? dateValue !== undefined ? dateValue : "xxx@xxx.xx"
                                                                : getTableDate(dateValue, "DD/MM/YYYY", ".", "xx.xx.xxxx")
                                                        )}
                                                    </p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}

                </div>
            </div>
            {/* 
                Download link - only show when the final stage (ahaettumatSkrad) is complete
                and we're not in a loading state
            */}
            {
                ahaettumatSkrad && link && !loading &&
                <div className="KCL_KYCSurveyProgressBar__link-wrapper">
                    <Link icon="pdf" href={link}>Sækja áreiðanleikakönnun</Link>
                </div>
            }
        </div>
    );
};

export default KYCSurveyProgressBar;

interface IStageProps {
    finished: boolean; // Whether this stage has been completed
    stageNumber: number; // The stage number to display (1, 2, 3, 4)
}

/**
 * StageIcon Component
 * 
 * Displays either:
 * - A blue check circle if the stage is finished
 * - A numbered circle (with the stage number overlaid) if the stage is not yet finished
 */
const StageIcon: React.FC<IStageProps> = ({ finished, stageNumber }) => {
    return (
        <div className="KYC_StageIcon">
            {finished ? (
                // Show check icon for completed stages
                <FontAwesomeIcon className="fa-icon stage finished" icon={faCheckCircle} />
            ) : (
                // Show numbered circle for incomplete stages
                // The number is positioned absolutely over the circle icon
                <>
                    <span className="stage_number">{stageNumber}</span>
                    <FontAwesomeIcon className="fa-icon stage" icon={faCircle} />
                </>
            )}
        </div>
    );
};